import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'

const fmt = n => n || n === 0 ? Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'

// Default section scaffold — titles fixed, content always starts empty.
// Each project fills in their own wording; company legal teams differ.
const DEFAULT_SECTIONS = [
  { key: 'parties',       title: '1. Parties' },
  { key: 'recitals',      title: '2. Recitals / Background' },
  { key: 'scope',         title: '3. Scope of Work' },
  { key: 'value_payment', title: '4. Contract Value & Payment Terms' },
  { key: 'retention',     title: '5. Retention & Advance Payment' },
  { key: 'program',       title: '6. Program / Schedule' },
  { key: 'variations',    title: '7. Variations' },
  { key: 'termination',   title: '8. Termination' },
  { key: 'warranties',    title: '9. Warranties & Defects Liability' },
  { key: 'insurance',     title: '10. Insurance & Indemnity' },
  { key: 'disputes',      title: '11. Dispute Resolution' },
  { key: 'signatures',    title: '12. Signatures' },
]

export default function ContractDocument({ subcontractId, docType = 'material', onClose }) {
  const isEquipment = docType === 'equipment'
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [contract, setContract] = useState(null)
  const [org, setOrg] = useState(null)
  const [project, setProject] = useState(null)
  const [sections, setSections] = useState([])
  const [editing, setEditing] = useState(true) // toggle between edit and print-preview

  useEffect(() => { load() }, [subcontractId])

  async function load() {
    setLoading(true)
    const table = isEquipment ? 'equipment_contracts' : 'subcontracts'
    const poJoin = isEquipment ? 'rental_equipment_pos(po_number)' : 'purchase_orders(po_number)'
    const sectionsTable = isEquipment ? 'equipment_contract_sections' : 'subcontract_sections'
    const fkField = isEquipment ? 'equipment_contract_id' : 'subcontract_id'

    const { data: sc } = await supabase.from(table).select(`*, ${poJoin}`).eq('id', subcontractId).single()
    if (!sc) { setLoading(false); return }
    setContract(sc)

    const { data: proj } = await supabase.from('projects').select('*, organizations(*)').eq('id', sc.project_id).single()
    setProject(proj)
    setOrg(proj?.organizations || null)

    let { data: sec } = await supabase.from(sectionsTable).select('*').eq(fkField, subcontractId).order('sort_order')
    if (!sec || sec.length === 0) {
      // First time opening this contract's document — seed the default scaffold
      const seedRows = DEFAULT_SECTIONS.map((s, i) => ({
        [fkField]: subcontractId, section_key: s.key, title: s.title, content: '', sort_order: i, is_custom: false,
      }))
      const { data: inserted } = await supabase.from(sectionsTable).insert(seedRows).select()
      sec = inserted || seedRows
    }
    setSections(sec)
    setLoading(false)
  }

  function updateSectionContent(id, content) {
    setSections(prev => prev.map(s => s.id === id ? { ...s, content } : s))
  }

  function updateSectionTitle(id, title) {
    setSections(prev => prev.map(s => s.id === id ? { ...s, title } : s))
  }

  async function saveSections() {
    setSaving(true)
    const sectionsTable = isEquipment ? 'equipment_contract_sections' : 'subcontract_sections'
    for (const s of sections) {
      await supabase.from(sectionsTable)
        .update({ title: s.title, content: s.content, updated_at: new Date().toISOString() })
        .eq('id', s.id)
    }
    setSaving(false)
  }

  async function addCustomSection() {
    const sortOrder = sections.length
    const sectionsTable = isEquipment ? 'equipment_contract_sections' : 'subcontract_sections'
    const fkField = isEquipment ? 'equipment_contract_id' : 'subcontract_id'
    const { data, error } = await supabase.from(sectionsTable).insert({
      [fkField]: subcontractId, section_key: 'custom_' + Date.now(), title: 'New Section', content: '', sort_order: sortOrder, is_custom: true,
    }).select().single()
    if (error) { alert('Error: ' + error.message); return }
    setSections(prev => [...prev, data])
  }

  async function removeSection(id) {
    if (!window.confirm('Remove this section?')) return
    const sectionsTable = isEquipment ? 'equipment_contract_sections' : 'subcontract_sections'
    await supabase.from(sectionsTable).delete().eq('id', id)
    setSections(prev => prev.filter(s => s.id !== id))
  }

  function handlePrint() {
    if (editing) setEditing(false)
    setTimeout(() => window.print(), 50)
  }

  if (loading) {
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(15,16,23,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ background: '#fff', borderRadius: 10, padding: '24px 32px', fontSize: 13, color: '#6B7280' }}>Loading contract…</div>
      </div>
    )
  }
  if (!contract) {
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(15,16,23,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ background: '#fff', borderRadius: 10, padding: '24px 32px', fontSize: 13, color: '#DC2626' }}>
          Contract not found.
          <button onClick={onClose} style={{ display: 'block', marginTop: 12, fontSize: 12, color: '#6B7280', cursor: 'pointer', background: 'none', border: 'none' }}>Close</button>
        </div>
      </div>
    )
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 500, background: '#43464D', overflowY: 'auto' }}>
      {/* Toolbar — hidden on print */}
      <div className="print-hide" style={{ position: 'sticky', top: 0, zIndex: 10, background: '#1C1E24', padding: '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>
          {isEquipment ? 'Equipment Rental Agreement' : 'Subcontract Agreement'} — {contract.contract_no}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setEditing(e => !e)}
            style={{ padding: '7px 16px', borderRadius: 6, background: editing ? '#374151' : '#1B3A6B', color: '#fff', border: 'none', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>
            {editing ? '👁 Preview' : '✎ Edit'}
          </button>
          {editing && (
            <button onClick={saveSections} disabled={saving}
              style={{ padding: '7px 16px', borderRadius: 6, background: '#1A7A4A', color: '#fff', border: 'none', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', opacity: saving ? .6 : 1 }}>
              {saving ? 'Saving…' : '💾 Save'}
            </button>
          )}
          <button onClick={handlePrint}
            style={{ padding: '7px 16px', borderRadius: 6, background: '#1A7A4A', color: '#fff', border: 'none', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>
            🖨 Print / Save PDF
          </button>
          <button onClick={onClose}
            style={{ padding: '7px 16px', borderRadius: 6, background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,.25)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>
            ✕ Close
          </button>
        </div>
      </div>

      {/* Document page */}
      <div className="print-page" style={{ width: 794, minHeight: 1123, margin: '24px auto', background: '#fff', boxShadow: '0 4px 24px rgba(0,0,0,.3)', padding: '40px 48px', boxSizing: 'border-box', fontFamily: "'Segoe UI', Arial, sans-serif", color: '#0F1017' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '3px solid #1B3A6B', paddingBottom: 16, marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {org?.logo_url ? (
              <img src={org.logo_url} alt="logo" style={{ width: 56, height: 56, objectFit: 'contain' }} />
            ) : (
              <div style={{ width: 56, height: 56, borderRadius: 8, background: '#1B3A6B', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 20, fontWeight: 800 }}>
                {org?.name?.charAt(0) || 'C'}
              </div>
            )}
            <div>
              <div style={{ fontSize: 15, fontWeight: 800, color: '#1B3A6B' }}>{org?.name || 'Company Name'}</div>
              {org?.cr_number && <div style={{ fontSize: 9.5, color: '#9CA3AF' }}>CR: {org.cr_number}</div>}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 17, fontWeight: 800, color: '#1B3A6B' }}>{isEquipment ? 'EQUIPMENT RENTAL AGREEMENT' : 'SUBCONTRACT AGREEMENT'}</div>
            <div style={{ fontSize: 11, color: '#6B7280', marginTop: 4 }}>No. <span style={{ fontWeight: 700, color: '#0F1017', fontFamily: 'monospace' }}>{contract.contract_no}</span></div>
            <div style={{ fontSize: 11, color: '#6B7280' }}>Date: <span style={{ fontWeight: 700, color: '#0F1017' }}>{contract.contract_date || '—'}</span></div>
          </div>
        </div>

        {/* Summary strip */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 24, flexWrap: 'wrap' }}>
          {[
            ['Project', project?.name],
            [isEquipment ? 'Vendor' : 'Subcontractor', contract.contractor_name],
            ['Contract Value', contract.contract_value ? `${fmt(contract.contract_value)} ${contract.currency || 'SAR'}` : null],
            ['Retention', contract.retention_pct != null ? `${contract.retention_pct}%` : null],
            ['Payment Terms', contract.payment_terms != null ? `${contract.payment_terms} days` : null],
            ['Linked PO', isEquipment ? contract.rental_equipment_pos?.po_number : contract.purchase_orders?.po_number],
          ].filter(([, v]) => v).map(([label, val]) => (
            <div key={label} style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 6, padding: '6px 12px', fontSize: 10.5 }}>
              <span style={{ color: '#6B7280' }}>{label}: </span><span style={{ fontWeight: 700, color: '#0F1017' }}>{val}</span>
            </div>
          ))}
        </div>

        {/* Sections */}
        {sections.map(s => (
          <div key={s.id} style={{ marginBottom: 22, breakInside: 'avoid' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              {editing ? (
                <input value={s.title} onChange={e => updateSectionTitle(s.id, e.target.value)}
                  className="print-hide-border"
                  style={{ fontSize: 13, fontWeight: 800, color: '#1B3A6B', border: 'none', borderBottom: '1px dashed #D1D5DB', outline: 'none', flex: 1, background: 'transparent', fontFamily: 'inherit' }} />
              ) : (
                <div style={{ fontSize: 13, fontWeight: 800, color: '#1B3A6B' }}>{s.title}</div>
              )}
              {editing && s.is_custom && (
                <button onClick={() => removeSection(s.id)} className="print-hide"
                  style={{ fontSize: 10, color: '#DC2626', background: 'none', border: 'none', cursor: 'pointer' }}>✕ Remove</button>
              )}
            </div>
            {editing ? (
              <textarea value={s.content} onChange={e => updateSectionContent(s.id, e.target.value)}
                placeholder="Enter this section's wording…" rows={4}
                style={{ width: '100%', boxSizing: 'border-box', fontSize: 11.5, lineHeight: 1.6, color: '#374151', border: '1px solid #E5E7EB', borderRadius: 6, padding: '8px 10px', resize: 'vertical', fontFamily: 'inherit', outline: 'none' }} />
            ) : (
              <div style={{ fontSize: 11.5, lineHeight: 1.7, color: '#374151', whiteSpace: 'pre-wrap', minHeight: 20 }}>
                {s.content || <span style={{ color: '#C4C9D1', fontStyle: 'italic' }}>— not yet completed —</span>}
              </div>
            )}
          </div>
        ))}

        {editing && (
          <button onClick={addCustomSection} className="print-hide"
            style={{ marginTop: 8, padding: '8px 16px', borderRadius: 6, border: '1px dashed #9CA3AF', background: '#F9FAFB', color: '#6B7280', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            + Add Custom Section
          </button>
        )}

        {/* Footer */}
        <div style={{ marginTop: 30, paddingTop: 10, borderTop: '1px solid #E5E7EB', fontSize: 8.5, color: '#9CA3AF', textAlign: 'center' }}>
          {org?.name || 'CostSpace'} — Generated by CostSpace Pro
        </div>
      </div>

      <style>{`
        @media print {
          .print-hide, .print-hide-border { display: none !important; }
          textarea { border: none !important; padding: 0 !important; resize: none !important; }
          body { margin: 0; }
          .print-page { box-shadow: none !important; margin: 0 !important; width: 100% !important; }
        }
        @page { size: A4; margin: 0; }
      `}</style>
    </div>
  )
}
